import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { ReactComponent as LeftArrowIcon } from '../../assets/images/icons/mevo/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/images/icons/right-arrow.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/icons/info.svg';
import { useIsMobile } from '../../helpers/useIsMobile';
import FreeSampleHeader from './FreeSampleHeader';
import { Button } from '../UI-CHAKRA/Button';
import { Input } from '../UI-CHAKRA/Input/Input';
import { MaskedInput, MASKS } from '../UI-CHAKRA/Input/MaskedInput';
import { FormControl } from '../UI-CHAKRA/FormControl';
import { FormLabel } from '../UI-CHAKRA/FormLabel';
import { Checkbox } from '../UI-CHAKRA/Checkbox';
import { CustomMenuItem, CustomSelect } from '../UI-CHAKRA/Select/CustomSelect';
import SearchAddressByCep from '../SearchAddressByCep';
import SelectState from '../SelectState';
import { Address } from '../../shared/cep';
import { FreeSampleDeliveryAddress, FreeSampleEstablishmentOption } from '../../interfaces/freeSample';

interface FreeSampleAddressStepProps {
  laboratoryName: string;
  productNames: string[];
  establishmentOptions: FreeSampleEstablishmentOption[];
  selectedEstablishmentId: number | null;
  onSelectEstablishment: (option: FreeSampleEstablishmentOption) => void;
  address: FreeSampleDeliveryAddress;
  onAddressChange: (address: FreeSampleDeliveryAddress) => void;
  confirmsAttendanceLocation: boolean;
  onConfirmsAttendanceLocationChange: (checked: boolean) => void;
  onBack: () => void;
  onConfirm: () => void;
  onClose: () => void;
  onEventEmission: (eventName: string, extraParams?: Record<string, unknown>) => void;
}

const FreeSampleAddressStep: React.FC<FreeSampleAddressStepProps> = ({
  laboratoryName,
  productNames,
  establishmentOptions,
  selectedEstablishmentId,
  onSelectEstablishment,
  address,
  onAddressChange,
  confirmsAttendanceLocation,
  onConfirmsAttendanceLocationChange,
  onBack,
  onConfirm,
  onClose,
  onEventEmission,
}) => {
  const isMobile = useIsMobile();
  const hasEmittedViewEventRef = useRef(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const summaryTitle = `${productNames.length} ${productNames.length === 1 ? 'amostra' : 'amostras'} de ${laboratoryName}`;

  useEffect(() => {
    if (hasEmittedViewEventRef.current) return;
    hasEmittedViewEventRef.current = true;
    onEventEmission('backoffice_campaign_amostra_gratis_endereco_view', { laboratory: laboratoryName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emitAddressClick = (continued: boolean) => {
    onEventEmission('backoffice_campaign_amostra_gratis_endereco_click', { continouSolicitacao: continued });
  };

  const handleConfirm = () => {
    emitAddressClick(true);
    onConfirm();
  };

  const handleBack = () => {
    emitAddressClick(false);
    onBack();
  };

  const handleClose = () => {
    onEventEmission('backoffice_campaign_amostra_gratis_endereco_cancelar_click');
    onClose();
  };

  const updateAddress = (patch: Partial<FreeSampleDeliveryAddress>) => {
    onAddressChange({ ...address, ...patch });
  };

  const onFindAddress = (foundAddress: Address) => {
    updateAddress({
      city: foundAddress.localidade,
      neighborhood: foundAddress.bairro,
      state: foundAddress.uf,
      address: foundAddress.logradouro,
    });
  };

  const isAddressValid =
    !!selectedEstablishmentId &&
    !!address.postalCode &&
    !!address.address &&
    (address.withoutNumber || !!address.number) &&
    !!address.neighborhood &&
    !!address.city &&
    !!address.state &&
    confirmsAttendanceLocation;

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      {isMobile ? (
        <Box flexShrink={0} borderBottom={'1px solid #E9ECEF'}>
          <Flex flexDirection={'row'} alignItems={'center'} gap={'8px'} padding={'20px'}>
            <LeftArrowIcon onClick={handleBack} cursor={'pointer'} />
            <Text fontSize={'14px'} fontWeight={700} color={'primary.main'}>
              Voltar
            </Text>
          </Flex>
        </Box>
      ) : (
        <FreeSampleHeader onClose={handleClose} />
      )}

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden" padding={'16px 20px'}>
        <Box mb={'16px'}>
          <Flex flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} gap={'12px'}>
            <Box>
              <Text fontSize={'12px'} color={'#5B6570'}>
                Solicitação
              </Text>
              <Text fontSize={'14px'} fontWeight={600} color={'#1E252B'} mt={'2px'}>
                {summaryTitle}
              </Text>
            </Box>
            <Flex
              as="button"
              type="button"
              flexShrink={0}
              alignItems={'center'}
              gap={'8px'}
              cursor={'pointer'}
              onClick={() => setIsSummaryExpanded((prev) => !prev)}
              aria-expanded={isSummaryExpanded}
            >
              <Text fontSize={'14px'} fontWeight={600} color={'#3D0E6B'}>
                {isSummaryExpanded ? 'Recolher' : 'Expandir'}
              </Text>
              {isSummaryExpanded ? (
                <ChevronUpIcon boxSize={'16px'} color={'#3D0E6B'} />
              ) : (
                <ChevronDownIcon boxSize={'16px'} color={'#3D0E6B'} />
              )}
            </Flex>
          </Flex>
          {isSummaryExpanded && (
            <Flex as="ul" flexDirection={'column'} gap={'6px'} pt={'12px'} listStyleType={'none'} m={0}>
              {productNames.map((name) => (
                <Flex as="li" key={name} flexDirection={'row'} gap={'8px'}>
                  <Text fontSize={'13px'} color={'#5B6570'}>
                    •
                  </Text>
                  <Text fontSize={'13px'} color={'#1E252B'}>
                    {name}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
        </Box>

        <Flex flexDirection={'row'} gap={'8px'} backgroundColor={'#EEF1FF'} borderRadius={'8px'} padding={'8px 16px'} mb={'20px'}>
          <Box flexShrink={0} mt={'2px'}>
            <InfoIcon width={'16px'} height={'16px'} fill="#5C2E8C" />
          </Box>
          <Text fontSize={'14px'} color={'#343A40'}>
            De acordo com a RDC 60/2009, a entrega da amostra grátis está restrita a hospital, ambulatório e consultório.
          </Text>
        </Flex>

        <Text fontSize={'16px'} fontWeight={700} color={'#343A40'}>
          Informe o endereço de entrega
        </Text>
        <Text fontSize={'14px'} color={'#6C757D'} mb={'16px'}>
          Preencha o endereço para receber a amostra:
        </Text>

        <Flex flexDirection={'column'} gap={'16px'}>
          <FormControl>
            <FormLabel>Local de atendimento</FormLabel>
            <CustomSelect
              placeholder={'Selecione'}
              value={selectedEstablishmentId ?? ''}
              displayValue={establishmentOptions.find((option) => option.id === selectedEstablishmentId)?.label ?? ''}
              isDisabled={establishmentOptions.length === 0}
            >
              {establishmentOptions.map((option) => (
                <CustomMenuItem
                  key={option.id}
                  value={option.id}
                  label={option.label}
                  onClick={() => onSelectEstablishment(option)}
                />
              ))}
            </CustomSelect>
          </FormControl>

          <SearchAddressByCep
            initialValue={address.postalCode}
            onFindAddress={onFindAddress}
            onChange={(value) => updateAddress({ postalCode: value })}
            required
            fullWidth
          />

          <FormControl>
            <FormLabel>Endereço*</FormLabel>
            <Input
              value={address.address}
              onChange={(e) => updateAddress({ address: e.target.value })}
              placeholder={'Endereço'}
            />
          </FormControl>

          <Flex gap={'12px'}>
            <Box w={'50%'}>
              <FormControl>
                <FormLabel>Número*</FormLabel>
                <MaskedInput
                  mask={MASKS.NUMBER_FIVE_DIGITS}
                  maskChar={''}
                  value={address.number}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateAddress({ number: e.target.value })}
                  placeholder={'Número'}
                  isDisabled={address.withoutNumber}
                />
                <Checkbox
                  mt={'8px'}
                  size={'sm'}
                  isChecked={address.withoutNumber}
                  onChange={(e) => updateAddress({ withoutNumber: e.target.checked, number: '' })}
                >
                  Sem número
                </Checkbox>
              </FormControl>
            </Box>
            <Box w={'50%'}>
              <FormControl>
                <FormLabel>Complemento</FormLabel>
                <Input
                  value={address.complement}
                  onChange={(e) => updateAddress({ complement: e.target.value })}
                  placeholder={'Complemento'}
                />
              </FormControl>
            </Box>
          </Flex>

          <FormControl>
            <FormLabel>Bairro*</FormLabel>
            <Input
              value={address.neighborhood}
              onChange={(e) => updateAddress({ neighborhood: e.target.value })}
              placeholder={'Bairro'}
            />
          </FormControl>

          <Flex gap={'12px'}>
            <Box w={'70%'}>
              <FormControl>
                <FormLabel>Cidade*</FormLabel>
                <Input
                  value={address.city}
                  onChange={(e) => updateAddress({ city: e.target.value })}
                  placeholder={'Cidade'}
                />
              </FormControl>
            </Box>
            <Box w={'30%'}>
              <SelectState value={address.state} onChange={(value) => updateAddress({ state: value })} required />
            </Box>
          </Flex>
        </Flex>

        <Checkbox
          mt={'20px'}
          isChecked={confirmsAttendanceLocation}
          onChange={(e) => onConfirmsAttendanceLocationChange(e.target.checked)}
          alignItems={'flex-start'}
        >
          <Text fontSize={'14px'} color={'#343A40'} ml={'8px'}>
            Confirmo que este é meu local de atendimento profissional.
          </Text>
        </Checkbox>
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={handleBack}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Voltar
            </Text>
          </Button>
          <Button
            onClick={handleConfirm}
            isDisabled={!isAddressValid}
            padding="10px 20px"
            backgroundColor={'primary.main'}
            borderRadius={'20px'}
            gap={'8px'}
            color={'#FFF'}
            fontSize={'14px'}
            _hover={{ backgroundColor: '#6B409C' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            rightIcon={<RightArrowIcon width={'16px'} height={'16px'} fill="#FFF" />}
          >
            Continuar
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default FreeSampleAddressStep;
